import React, { useEffect, useRef, useState, useCallback } from 'react';
import { TextField, Autocomplete as MuiAutocomplete } from '@mui/material';

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string, addressComponents: AddressComponents) => void;
  label?: string;
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

export interface AddressComponents {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

declare global {
  interface Window {
    google: any;
  }
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  label = 'Street Address',
  fullWidth = true,
  required = false,
  disabled = false,
  error = false,
  helperText = '',
}) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [autocompleteService, setAutocompleteService] = useState<any>(null);
  const [placesService, setPlacesService] = useState<any>(null);
  const [predictions, setPredictions] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState(value);
  const predictionsRef = useRef<any[]>([]);

  const initializeServices = () => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }
    
    // Create AutocompleteService and PlacesService
    const service = new window.google.maps.places.AutocompleteService();
    const places = new window.google.maps.places.PlacesService(document.createElement('div'));
    
    setAutocompleteService(service);
    setPlacesService(places);
    setScriptLoaded(true);
  };

  // Load Google Places API
  useEffect(() => {
    // Check if Google API is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      initializeServices();
      return;
    }

    // Load Google Places API if not already loaded
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.warn('Google Maps API key not found. Address autocomplete will not work.');
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      // Script is already in the page, wait for it to load
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkGoogle);
          initializeServices();
        }
      }, 100);
      
      return () => clearInterval(checkGoogle);
    }

    // Load the script dynamically
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Wait for places API to be available
      const checkPlaces = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkPlaces);
          initializeServices();
        }
      }, 50);
      // Timeout after 10 seconds
      setTimeout(() => clearInterval(checkPlaces), 10000);
    };
    script.onerror = () => {
      console.error('Failed to load Google Places API');
    };
    document.head.appendChild(script);
  }, []);

  // Expand common directional abbreviations to improve Google autocomplete matching
  const expandDirectionalAbbreviations = (input: string): string => {
    // Common directional abbreviations and their full forms
    const abbreviations: { [key: string]: string } = {
      '\\bN\\b': 'North',
      '\\bS\\b': 'South',
      '\\bE\\b': 'East',
      '\\bW\\b': 'West',
      '\\bNE\\b': 'Northeast',
      '\\bNW\\b': 'Northwest',
      '\\bSE\\b': 'Southeast',
      '\\bSW\\b': 'Southwest',
    };

    let expanded = input;
    // Replace abbreviations with full words (case-insensitive, whole word only)
    Object.entries(abbreviations).forEach(([abbr, full]) => {
      const regex = new RegExp(abbr, 'gi');
      expanded = expanded.replace(regex, full);
    });

    return expanded;
  };

  // Handle input changes and fetch predictions
  const handleInputChange = useCallback((newValue: string) => {
    setInputValue(newValue);

    if (!newValue || newValue.length < 3) {
      setPredictions([]);
      return;
    }

    if (!autocompleteService) {
      return;
    }

    // Expand directional abbreviations before sending to Google
    const expandedInput = expandDirectionalAbbreviations(newValue);

    // Use AutocompleteService to get predictions
    autocompleteService.getPlacePredictions(
      {
        input: expandedInput,
        componentRestrictions: { country: 'us' },
        types: ['address'],
      },
      (predictions: any[], status: string) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const formattedPredictions = predictions.map(p => ({
            description: p.description,
            placeId: p.place_id,
          }));
          predictionsRef.current = formattedPredictions;
          setPredictions(formattedPredictions);
        } else {
          setPredictions([]);
        }
      }
    );
  }, [autocompleteService]);

  // Handle selection from autocomplete
  const handleSelect = useCallback((description: string | null) => {
    if (!description || !placesService) {
      return;
    }

    // Find the selected prediction
    const selectedPrediction = predictionsRef.current.find(p => p.description === description);
    if (!selectedPrediction) {
      return;
    }

    // Get place details using PlacesService
    placesService.getDetails(
      {
        placeId: selectedPrediction.placeId,
        fields: ['address_components', 'formatted_address', 'geometry'],
      },
      (place: any, status: string) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          // Parse address components
          const addressComponents: AddressComponents = {
            street: '',
            city: '',
            state: '',
            zipCode: '',
          };

          place.address_components.forEach((component: any) => {
            const types = component.types;
            
            if (types.includes('street_number')) {
              addressComponents.street = component.long_name;
            } else if (types.includes('route')) {
              addressComponents.street = `${addressComponents.street} ${component.long_name}`.trim();
            } else if (types.includes('locality')) {
              addressComponents.city = component.long_name;
            } else if (types.includes('administrative_area_level_1')) {
              addressComponents.state = component.short_name;
            } else if (types.includes('postal_code')) {
              addressComponents.zipCode = component.long_name;
            }
          });

          // Extract coordinates from geometry
          if (place.geometry && place.geometry.location) {
            addressComponents.coordinates = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };
          }

          setInputValue(place.formatted_address || '');
          setPredictions([]);
          onChange(place.formatted_address || '', addressComponents);
        }
      }
    );
  }, [placesService, onChange]);

  // Sync input value with parent value prop
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <MuiAutocomplete
      fullWidth={fullWidth}
      options={predictions.map(p => p.description)}
      value={inputValue}
      onInputChange={(e, newValue) => handleInputChange(newValue)}
      onChange={(e, newValue) => handleSelect(newValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          disabled={disabled}
          error={error}
          helperText={helperText}
        />
      )}
      loading={predictions.length === 0 && inputValue.length >= 3}
      ListboxProps={{
        style: { maxHeight: 200 }
      }}
    />
  );
};

export default AddressAutocomplete;
